// CategoryPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GetProductsQuery, Product } from "utils";
import { Loading } from "../Loading";
import { useUtility } from "utils/context/UtilityContext";
import { motion } from "framer-motion";
import { Header } from "../Header";
import { Footer } from "../footer";
import { Subtitle } from "../Subtitle";
import { ProviderList } from "./ProviderList";
import { Button, Typography, Box, Pagination } from "@mui/material";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { getProducts } from "utils/api/private/ProductApiService";
import { PaginatedApiResponse } from "utils/helper/ApiResponses";

export const CategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = Number(searchParams.get("page")) || 1;
  const initialLimit = Number(searchParams.get("limit")) || 12;
  const initialSortBy = searchParams.get("sortBy") || "priceUSD";
  const initialOrder = (searchParams.get("order") as "asc" | "desc") || "asc";

  const [query, setQuery] = useState<GetProductsQuery>({
    category: category || "",
    page: initialPage,
    limit: initialLimit,
    sortBy: initialSortBy,
    order: initialOrder,
    groupBy: "provider",
  });

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState<number>(0);
  const { marginBottomHeader } = useUtility();
  const navigate = useNavigate();

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (query.limit || 12))),
    [total, query.limit]
  );

  const fetchProducts = useCallback(async () => {
    if (!query.category && category) {
      setQuery((prev) => ({ ...prev, category, page: 1 }));
      return;
    }

    setLoading(true);
    try {
      const result: PaginatedApiResponse<Product> = await getProducts(query);
      console.log(result.meta);
      if (
        result.meta.total &&
        query.page > Math.ceil(result.meta.total / query.limit)
      ) {
        setQuery((prev) => ({
          ...prev,
          page: Math.max(1, Math.ceil(result.meta.total / prev.limit)),
        }));
        return;
      }
      setProducts(result.data || []);
      setTotal(result.meta.total || 0);
    } catch (error) {
      console.error("[CategoryPage] Fetch error:", error);
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, category]);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    });
    setSearchParams(params, { replace: true });
  }, [query, setSearchParams]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  if (loading) return <Loading />;

  const handleSortToggle = () =>
    setQuery((prev) => ({
      ...prev,
      order: prev.order === "asc" ? "desc" : "asc",
      page: 1,
    }));

  const handleBack = () => navigate(-1);
  const handleSelectProvider = (provider: string) =>
    navigate(`/provider/${provider}`);

  const handlePageChange = (_: any, newPage: number) =>
    setQuery((prev) => ({ ...prev, page: newPage }));

  const categories = Array.from(new Set(products.map((p) => p.category)));

  const providersByCategory = categories.map((cat) => {
    const providersSet = new Set(
      products.filter((p) => p.category === cat).map((p) => p.provider)
    );
    const providers = Array.from(providersSet).sort((a, b) =>
      (query.order || "asc") === "asc" ? a.localeCompare(b) : b.localeCompare(a)
    );

    const providerImages = Array.from(
      new Map(
        products
          .filter((p) => p.category === cat)
          .map((p) => [p.provider, { provider: p.provider, image: p.image }])
      ).values()
    );

    return { category: cat, providers, providerImages };
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll`}
    >
      <Header />
      <div className="flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll p-8 w-full">
        <Box className="w-full flex items-center justify-between">
          <Button variant="contained" onClick={handleSortToggle}>
            Trier par nom ({(query.order || "asc") === "asc" ? "A-Z" : "Z-A"})
          </Button>

          {/* 🔢 Pagination (backend-driven) */}
          <Pagination
            count={pageCount}
            page={query.page}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>

        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 mt-8">
            <SentimentDissatisfiedIcon
              style={{ fontSize: 50, color: "gray" }}
            />
            <Typography variant="h6" className="text-gray-700">
              Rien à trouver sur cette page 😔
            </Typography>
            <Button variant="contained" onClick={handleBack}>
              Aller à la page précédente
            </Button>
          </div>
        ) : (
          providersByCategory.map(({ category, providers, providerImages }) => (
            <div key={category} className="w-full">
              <Subtitle title={category} />
              <ProviderList
                providers={providers}
                providerImages={providerImages}
                onSelectProvider={handleSelectProvider}
              />
            </div>
          ))
        )}

      </div>
      <Footer />
    </motion.div>
  );
};
